import { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import LoadingState from '@/components/base/LoadingState';
import Swal from 'sweetalert2';
import SuperAdminLayout from '@/components/feature/SuperAdminLayout';
import SAHeader from '@/components/feature/SAHeader';
import { requireSuperAdmin } from '@/middleware/superAdminGuard';
import { supabase } from '@/lib/supabaseClient';
import { getAllTenantsStorageMap, formatStorageBytes, formatStorageMb } from '@/lib/getStorageUsage';
import TenantModal, { type Tenant } from './components/TenantModal';
import StatusBadge from '@/components/base/StatusBadge';
import { logAudit } from '@/lib/logAudit';
import { clearModuleCache } from '@/lib/hasModuleAccess';

// ── Styles ────────────────────────────────────────────────────────────────────

const PLAN_STYLES: Record<string, { badge: string; text: string }> = {
  free:       { badge: 'bg-slate-100 text-slate-500',   text: 'Free' },
  pro:        { badge: 'bg-amber-50 text-amber-700',    text: 'Pro' },
  enterprise: { badge: 'bg-emerald-50 text-emerald-700',text: 'Enterprise' },
};

const STATUS_OPTS = [
  { value: 'active',    label: '✅  Active' },
  { value: 'trial',     label: '🔵  Trial' },
  { value: 'suspended', label: '⚠️  Suspended' },
  { value: 'cancelled', label: '❌  Cancelled' },
];

const STATUSES = ['active', 'trial', 'suspended', 'cancelled'];

const SW = { background: '#ffffff', color: '#0f172a', confirmButtonColor: '#2563eb' };

// ── Component ─────────────────────────────────────────────────────────────────

interface PlanOption { id: string; name: string; price_monthly: number }

export default function SATenants() {
  const navigate = useNavigate();

  const [tenants, setTenants]           = useState<Tenant[]>([]);
  const [plans, setPlans]               = useState<PlanOption[]>([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [planFilter, setPlanFilter]     = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [storageMap, setStorageMap]     = useState<Record<string, number>>({});

  // modal
  const [modalOpen, setModalOpen]       = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [isNew, setIsNew]               = useState(false);

  // ── Load ───────────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [{ data: tData }, { data: pData }, storageResult] = await Promise.all([
      supabase.from('saas_tenants').select('*').order('created_at', { ascending: false }),
      supabase.from('saas_plans').select('id, name, price_monthly').order('price_monthly'),
      getAllTenantsStorageMap(),
    ]);
    setTenants((tData ?? []) as Tenant[]);
    setPlans((pData ?? []) as PlanOption[]);
    setStorageMap(storageResult);
    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const ok = await requireSuperAdmin();
      if (!ok) { navigate('/login'); return; }
      await fetchAll();
    };
    init();
  }, [navigate, fetchAll]);

  // ── Filters ────────────────────────────────────────────────────────────────
  const filtered = tenants.filter((t) => {
    const q = search.toLowerCase();
    const matchSearch  = t.name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q);
    const matchPlan    = planFilter   === 'all' || t.plan_code === planFilter;
    const matchStatus  = statusFilter === 'all' || t.status    === statusFilter;
    return matchSearch && matchPlan && matchStatus;
  });

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleNew  = () => { setSelectedTenant(null); setIsNew(true);  setModalOpen(true); };
  const handleEdit = (t: Tenant) => { setSelectedTenant(t); setIsNew(false); setModalOpen(true); };

  const handleSave = async (formData: Partial<Tenant> & { name: string; slug: string }) => {
    if (isNew) {
      const { data: created, error } = await supabase
        .from('saas_tenants')
        .insert({
          name: formData.name, slug: formData.slug,
          plan_code: formData.plan_code ?? 'free', status: formData.status ?? 'trial',
          max_users: formData.max_users ?? 1,
          max_active_contracts: formData.max_active_contracts ?? 10,
          storage_quota_mb: formData.storage_quota_mb ?? 512,
          storage_used_mb: 0,
        })
        .select().maybeSingle();
      if (error) { Swal.fire({ title: 'Error', text: error.message, icon: 'error', ...SW }); return; }
      if (created) setTenants((prev) => [created as Tenant, ...prev]);
      Swal.fire({ title: 'Tenant creado', icon: 'success', timer: 1600, showConfirmButton: false, ...SW });
    } else {
      if (!selectedTenant) return;
      const { data: updated, error } = await supabase
        .from('saas_tenants')
        .update({
          name: formData.name, slug: formData.slug, plan_code: formData.plan_code,
          status: formData.status, max_users: formData.max_users,
          max_active_contracts: formData.max_active_contracts,
          storage_quota_mb: formData.storage_quota_mb,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedTenant.id).select().maybeSingle();
      if (error) { Swal.fire({ title: 'Error', text: error.message, icon: 'error', ...SW }); return; }
      if (updated) setTenants((prev) => prev.map((t) => t.id === (updated as Tenant).id ? (updated as Tenant) : t));
      Swal.fire({ title: 'Cambios guardados', icon: 'success', timer: 1500, showConfirmButton: false, ...SW });
    }
    setModalOpen(false);
  };

  // Status change
  const handleChangeStatus = async (tenant: Tenant) => {
    const opts = STATUS_OPTS.reduce<Record<string, string>>((acc, o) => { acc[o.value] = o.label; return acc; }, {});
    const { value: newStatus } = await Swal.fire({
      title: `Status — "${tenant.name}"`,
      input: 'select',
      inputOptions: opts,
      inputValue: tenant.status,
      showCancelButton: true,
      confirmButtonText: 'Aplicar',
      cancelButtonText: 'Cancelar',
      ...SW,
    });
    if (!newStatus || newStatus === tenant.status) return;
    const { error } = await supabase
      .from('saas_tenants')
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq('id', tenant.id);
    if (error) { Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return; }
    setTenants((prev) => prev.map((t) => t.id === tenant.id ? { ...t, status: newStatus } : t));
    Swal.fire({ title: 'Status actualizado', icon: 'success', timer: 1400, showConfirmButton: false, ...SW });
  };

  // Plan change
  const handleChangePlan = async (tenant: Tenant) => {
    const planOpts = plans.reduce<Record<string, string>>((acc, p) => {
      acc[p.id] = `${p.name}${Number(p.price_monthly) === 0 ? ' (Free)' : ` — $${p.price_monthly}/mo`}`;
      return acc;
    }, {});

    const { value: newPlan } = await Swal.fire({
      title: `Plan — "${tenant.name}"`,
      html: `<p style="color:#64748b;font-size:13px">Plan actual: <strong style="color:#0f172a">${tenant.plan_code}</strong></p>`,
      input: 'select',
      inputOptions: planOpts,
      inputValue: tenant.plan_code,
      showCancelButton: true,
      confirmButtonText: 'Cambiar plan',
      cancelButtonText: 'Cancelar',
      ...SW,
    });
    if (!newPlan || newPlan === tenant.plan_code) return;

    // Get limits from selected plan
    const { data: planData } = await supabase
      .from('saas_plans')
      .select('max_users, max_contracts, storage_mb')
      .eq('id', newPlan)
      .maybeSingle();

    const updates: Record<string, unknown> = {
      plan_code: newPlan,
      updated_at: new Date().toISOString(),
    };
    if (planData) {
      updates.max_users = planData.max_users;
      updates.max_active_contracts = planData.max_contracts;
      updates.storage_quota_mb = planData.storage_mb;
    }

    const { error } = await supabase.from('saas_tenants').update(updates).eq('id', tenant.id);
    if (error) { Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return; }
    setTenants((prev) => prev.map((t) =>
      t.id === tenant.id ? { ...t, plan_code: newPlan, ...(planData ? {
        max_users: planData.max_users,
        max_active_contracts: planData.max_contracts,
        storage_quota_mb: planData.storage_mb,
      } : {}) } : t
    ));
    Swal.fire({
      icon: 'success',
      title: 'Plan actualizado',
      text: `"${tenant.name}" ahora está en el plan ${newPlan}`,
      timer: 1600,
      showConfirmButton: false,
      ...SW,
    });
  };

  // Impersonate
  const handleImpersonate = async (tenant: Tenant) => {
    const result = await Swal.fire({
      title: `Impersonar "${tenant.name}"`,
      html: `
        <p style="color:#64748b;font-size:13px;margin-bottom:8px">Accederás al panel de Admin como si fueras este estudio.</p>
        <div style="background:#fef3c7;border:1px solid #fde68a;border-radius:8px;padding:10px 14px;text-align:left">
          <p style="color:#92400e;font-size:12px;font-weight:600;margin:0 0 4px">⚠️ Modo impersonación</p>
          <p style="color:#b45309;font-size:12px;margin:0">Para salir, usa el banner que aparece en la parte superior del Admin.</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Entrar al Admin',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#7c3aed',
      ...SW,
    });
    if (!result.isConfirmed) return;

    localStorage.setItem('impersonated_tenant_id', tenant.id);
    localStorage.setItem('impersonated_tenant_name', tenant.name);
    localStorage.setItem('impersonated_tenant_slug', tenant.slug);
    localStorage.setItem('impersonated_from_sa', 'true');
    logAudit({ action: 'impersonation_start', entity: 'tenant', entityId: tenant.id, entityName: tenant.name }).catch(() => {});
    clearModuleCache();
    navigate('/admin');
  };

  // ── Stats ──────────────────────────────────────────────────────────────────
  const byStatus = (s: string) => tenants.filter((t) => t.status === s).length;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <SuperAdminLayout>
      <SAHeader
        title="Tenants"
        subtitle="Administración de estudios en la plataforma"
        actions={
          <button
            onClick={handleNew}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></span>
            Nuevo Tenant
          </button>
        }
      />

      <div className="px-4 md:px-8 py-4 md:py-7">

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-7">
          {[
            { label: 'Total',               value: tenants.length,                                   icon: 'ri-building-2-line',       color: 'text-blue-600 bg-blue-50' },
            { label: 'Active',              value: byStatus('active'),                               icon: 'ri-checkbox-circle-line',  color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Trial',               value: byStatus('trial'),                                icon: 'ri-time-line',             color: 'text-sky-600 bg-sky-50' },
            { label: 'Suspended/Cancelled', value: byStatus('suspended') + byStatus('cancelled'),   icon: 'ri-error-warning-line',    color: 'text-amber-600 bg-amber-50' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl px-4 py-3.5 border border-slate-200">
              <div className="flex items-center justify-between mb-1">
                <p className="text-slate-500 text-xs">{s.label}</p>
                <span className={`w-6 h-6 flex items-center justify-center rounded-md ${s.color}`}>
                  <i className={`${s.icon} text-xs`}></i>
                </span>
              </div>
              <p className="text-slate-900 text-2xl font-bold">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          {/* Search */}
          <div className="relative min-w-[220px] flex-1 max-w-sm">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre o slug…"
              className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-4 py-2 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-blue-400 transition-colors"
            />
          </div>

          {/* Plan filter */}
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1">
            <button
              onClick={() => setPlanFilter('all')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${planFilter === 'all' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-900'}`}
            >All</button>
            {plans.map((p) => (
              <button key={p.id} onClick={() => setPlanFilter(p.id)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-colors cursor-pointer whitespace-nowrap ${planFilter === p.id ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                {p.name}
              </button>
            ))}
          </div>

          {/* Status filter */}
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${statusFilter === 'all' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-900'}`}
            >All</button>
            {STATUSES.map((s) => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-colors cursor-pointer whitespace-nowrap ${statusFilter === s ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                {s}
              </button>
            ))}
          </div>

          <span className="text-slate-400 text-sm ml-auto whitespace-nowrap">
            {filtered.length} de {tenants.length} tenants
          </span>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? <LoadingState /> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[920px]">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    {['Tenant', 'Status', 'Plan', 'Storage', 'Creado', 'Acciones'].map((h) => (
                      <th key={h} className={`text-slate-400 text-xs font-semibold uppercase tracking-wider px-5 py-3.5 ${h === 'Acciones' ? 'text-right' : 'text-left'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((t) => {
                    const realUsedBytes  = storageMap[t.id] ?? 0;
                    const realUsedMb     = realUsedBytes / (1024 * 1024);
                    const pct = t.storage_quota_mb > 0
                      ? Math.min(100, Math.round((realUsedMb / t.storage_quota_mb) * 100))
                      : 0;
                    const barColor = pct > 80 ? 'bg-red-400' : pct > 50 ? 'bg-amber-400' : 'bg-emerald-400';
                    const ps = PLAN_STYLES[t.plan_code] ?? { badge: 'bg-slate-100 text-slate-500', text: t.plan_code };

                    return (
                      <tr key={t.id} className="hover:bg-slate-50/60 transition-colors group">

                        {/* Tenant */}
                        <td className="px-5 py-3.5">
                          <Link to={`/sa/tenants/${t.id}`} className="flex items-center gap-3">
                            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gradient-to-br from-blue-100 to-blue-200 text-blue-700 text-sm font-bold shrink-0 group-hover:from-blue-200 group-hover:to-blue-300 transition-all">
                              {t.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="text-slate-800 text-sm font-semibold group-hover:text-blue-600 transition-colors whitespace-nowrap">{t.name}</p>
                              <p className="text-slate-400 text-xs font-mono">{t.slug}</p>
                            </div>
                          </Link>
                        </td>

                        {/* Status — clickable badge */}
                        <td className="px-5 py-3.5">
                          <button
                            onClick={() => handleChangeStatus(t)}
                            className="cursor-pointer hover:opacity-75 transition-opacity"
                            title="Cambiar status"
                          >
                            <StatusBadge status={t.status} />
                          </button>
                        </td>

                        {/* Plan — clickable badge */}
                        <td className="px-5 py-3.5">
                          <button
                            onClick={() => handleChangePlan(t)}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold uppercase cursor-pointer hover:opacity-75 transition-opacity ${ps.badge}`}
                            title="Cambiar plan"
                          >
                            <i className="ri-price-tag-3-line text-[10px]"></i>
                            {t.plan_code}
                          </button>
                        </td>

                        {/* Storage */}
                        <td className="px-5 py-3.5">
                          <div className="w-36">
                            <div className="flex items-center justify-between text-[10px] mb-1.5">
                              <span className="text-slate-600 font-medium">{formatStorageBytes(storageMap[t.id] ?? 0)}</span>
                              <span className="text-slate-300">{formatStorageMb(t.storage_quota_mb)}</span>
                            </div>
                            <div className="w-full bg-slate-100 rounded-full h-1.5">
                              <div className={`h-1.5 rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
                            </div>
                            <p className="text-slate-300 text-[10px] mt-0.5 text-right">{pct}%</p>
                          </div>
                        </td>

                        {/* Created at */}
                        <td className="px-5 py-3.5">
                          <p className="text-slate-500 text-xs whitespace-nowrap">
                            {new Date(t.created_at).toLocaleDateString('es', { day: 'numeric', month: 'short', year: '2-digit' })}
                          </p>
                        </td>

                        {/* Actions */}
                        <td className="px-5 py-3.5">
                          <div className="flex items-center justify-end gap-1">
                            {/* Detail */}
                            <Link
                              to={`/sa/tenants/${t.id}`}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all cursor-pointer whitespace-nowrap"
                              title="Ver detalle"
                            >
                              <i className="ri-eye-line"></i>
                              <span className="hidden xl:inline">Detail</span>
                            </Link>

                            {/* Modules */}
                            <Link
                              to={`/sa/tenants/${t.id}/modules`}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all cursor-pointer whitespace-nowrap"
                              title="Módulos"
                            >
                              <i className="ri-puzzle-line"></i>
                              <span className="hidden xl:inline">Modules</span>
                            </Link>

                            {/* Edit */}
                            <button
                              onClick={() => handleEdit(t)}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all cursor-pointer whitespace-nowrap"
                              title="Editar"
                            >
                              <i className="ri-pencil-line"></i>
                              <span className="hidden xl:inline">Edit</span>
                            </button>

                            {/* Impersonate */}
                            <button
                              onClick={() => handleImpersonate(t)}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-all cursor-pointer whitespace-nowrap"
                              title="Impersonar — entrar como este tenant"
                            >
                              <i className="ri-spy-line"></i>
                              <span className="hidden xl:inline">Enter</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {filtered.length === 0 && !loading && (
                    <tr>
                      <td colSpan={6} className="px-5 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-slate-100 text-slate-300">
                            <i className="ri-building-2-line text-2xl"></i>
                          </div>
                          <p className="text-slate-400 text-sm">
                            {tenants.length === 0 ? 'Sin tenants — crea el primero' : 'Sin resultados para los filtros actuales'}
                          </p>
                          {tenants.length === 0 && (
                            <button onClick={handleNew} className="mt-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                              Crear primer tenant
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-4 flex-wrap">
          <p className="text-slate-300 text-xs">Acciones rápidas:</p>
          {[
            { icon: 'ri-checkbox-circle-line', label: 'Clic en Status para cambiar' },
            { icon: 'ri-price-tag-3-line',     label: 'Clic en Plan para cambiar' },
            { icon: 'ri-spy-line text-violet-500', label: '"Enter" para impersonar' },
          ].map((l) => (
            <div key={l.label} className="flex items-center gap-1.5">
              <span className="w-3 h-3 flex items-center justify-center"><i className={`${l.icon} text-slate-300 text-xs`}></i></span>
              <span className="text-slate-400 text-xs">{l.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <TenantModal
          tenant={selectedTenant}
          isNew={isNew}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
        />
      )}
    </SuperAdminLayout>
  );
}
